import { useQuery } from "@apollo/client";
import { LoadingSpinner } from "pepsico-ds";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { reconstructSurveyOutcomes } from "../../../../components/campaign/campaignUtils";
import "../../../../components/common/campaignCard/CampaignCard.scss";
import "../../../../components/common/GridCard.scss";
import { CampaignSetupContext } from "../../../../context/CampaignSetupContext";
import { getCampaignListing } from "../../../../graphql/queries/getCampaignListing";
import { listSegments } from "../../../../graphql/queries/listSegments";
import { loadCampaignById } from "../../../../graphql/queries/loadCampaignById";
import { paths } from "../../../../routes/paths";
import { FeatureToggle, useFeatureToggle } from "../../../../utils/featureToggle";
import CampaignOutcomes from "./CampaignOutcomes";
import CampaignRulesSummary from "./CampaignRulesSummary";
import LocalizationList from "./LocalizationList";
import ProductContentList from "./ProductContentList";
import SegmentsLinkedCampaigns from "./SegmentsLinkedCampaigns";
import SummaryActivitySetup from "./SummaryActivitySetup";
import SummaryBasicSetup from "./SummaryBasicSetup";
import "./summaryPage.scss";
import SummaryWidgetEmbedCode from "./SummaryWidgetEmbedCode";

export default function SummaryPage({ campaignId, isOnPage = true }) {
  const { campaignState, setCampaignState, currentCampaignTypeConfig } =
    useContext(CampaignSetupContext);
  const [staticCampaignState, setStaticCampaignState] = useState({});
  const [segmentedContentsData, setSegmentedContentsData] = useState([]);
  const inputSchema = currentCampaignTypeConfig?.campaignSetup?.inputSchema || {};
  const contentSchema =
    currentCampaignTypeConfig?.contentSteps?.[0]?.inputSchema || {};
  const navigate = useNavigate();
  const location = useLocation();
  const showWidgetEmbedCode = useFeatureToggle(FeatureToggle.showWidgetEmbedCode);
  const enableRewardGroupsInCampaignListing = useFeatureToggle(
    FeatureToggle.enableRewardGroupsInCampaignListing
  );

  const { data: allCampaignsData, loading: loadingCampaigns } = useQuery(
    getCampaignListing,
    {
      fetchPolicy: "cache-first",
      variables: {
        includeRewardGroups: enableRewardGroupsInCampaignListing,
      },
    }
  );

  const { data: segmentsData, loading: loadingSegments } = useQuery(listSegments);
  const { data: campaignData } = useQuery(loadCampaignById, {
    variables: { id: campaignId },
    skip: !campaignId,
  });

  useEffect(() => {
    if (campaignData?.loadCampaignById) {
      const reconstructedData = reconstructSurveyOutcomes(
        campaignData.loadCampaignById
      );
      reconstructedData.store_groups = reconstructedData.storeGroups || undefined;
      isOnPage
        ? setCampaignState(reconstructedData)
        : setStaticCampaignState(reconstructedData);
    }
  }, [campaignData, setCampaignState, isOnPage]);

  const campaignStateData = isOnPage ? campaignState : staticCampaignState;
  const contentData = campaignStateData?.campaignContent?.contentData;

  const getSegmentNames = useCallback(
    (segmentIds) => {
      if (!segmentIds || !segmentsData) return [];
      return segmentIds
        .map((id) => segmentsData.listSegments.find((seg) => seg.id === id))
        .filter(Boolean)
        .map((seg) => seg.name);
    },
    [segmentsData]
  );

  useEffect(() => {
    if (campaignStateData.segmentedContents && segmentsData) {
      const updatedSegmentedContents = campaignStateData.segmentedContents
        .filter((content) => content?.segments?.[0] !== "")
        .map((segmentId) => {
          const segment = segmentsData.listSegments.find(
            (seg) => seg.id === segmentId.segments?.[0]
          );
          return {
            ...segmentId,
            segmentName: segment?.name || "Unknown Segment",
          };
        });

      setSegmentedContentsData(updatedSegmentedContents);
    }
  }, [campaignStateData, segmentsData, getSegmentNames]);

  const isViewScreen =
    location.pathname.includes("campaign-summary-view") || !isOnPage;

  const isSegmentSetRef = useRef(false);
  useEffect(() => {
    if (
      campaignStateData?.type === "gift_processing" &&
      !isViewScreen &&
      !isSegmentSetRef.current
    ) {
      isSegmentSetRef.current = true;
      for (let rule of campaignStateData.campaignRules) {
        if (rule.ruleDefinition?.slug === "user-segment-for-gift-processing") {
          campaignStateData.segments = rule.ruleData?.segment_ids || [];
        }
      }
    }
  }, [campaignStateData, isViewScreen]);

  const getBasePath = () => {
    const path = location.pathname.split("/");
    return path.slice(0, path.length - 1).join("/");
  };

  const handleBasicEditSection = (extraParams) => {
    let url = `${getBasePath()}/generic-setup`;
    if (extraParams) {
      url = `${url}#${extraParams}`;
    }
    navigate(url);
  };

  const handleActivityEditSection = () => {
    const isNewApproach =
      currentCampaignTypeConfig.steps?.some(
        (step) => step.slug === `${paths.acitivity_level_setup_new}`
      ) || false;

    if (isNewApproach) {
      navigate(`${getBasePath()}/activity-level-setup-new`);
    } else {
      navigate(`${getBasePath()}/activity-level-setup`);
    }
  };

  const handleOutcomesEditSection = () => {
    // Find the step that has "outcomes" in its values
    const outcomeStep = currentCampaignTypeConfig.steps?.find((step) =>
      step.values?.some((value) => value.type === "outcomes")
    );

    if (outcomeStep) {
      navigate(`${getBasePath()}/${outcomeStep.slug}`);
    }
  };

  const relatedCampaignsData =
    allCampaignsData?.listCampaigns?.filter((campaign) =>
      campaignStateData.relatedCampaigns?.includes(campaign.id)
    ) || [];

  return (
    <>
      {/* Basic Card */}
      <SummaryBasicSetup
        data={campaignStateData}
        isViewScreen={isViewScreen}
        editSectionHandler={() => handleBasicEditSection()}
        inputSchema={inputSchema}
        contentSchema={contentSchema}
        currentCampaignTypeConfig={currentCampaignTypeConfig}
      />
      {showWidgetEmbedCode && (
        <SummaryWidgetEmbedCode
          data={campaignStateData}
          isViewScreen={isViewScreen}
        />
      )}

      {/* Selected Contents */}
      {contentData?.l10n && (
        <LocalizationList
          data={contentData}
          isViewScreen={isViewScreen}
          contentSchema={contentSchema}
          editSectionHandler={() =>
            handleBasicEditSection("campaign-language-content-title")
          }
        />
      )}
      {contentData?.product_contents && (
        <ProductContentList
          editSectionHandler={() => handleBasicEditSection()}
          data={contentData.product_contents}
          contentSchema={contentSchema?.definitions?.product_content}
        />
      )}

      {/* Segments/Linked Campaigns Section */}
      {(segmentedContentsData.length > 0 || relatedCampaignsData.length > 0) && (
        <>
          {loadingCampaigns || loadingSegments ? (
            <div className="loading-container">
              <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
            </div>
          ) : (
            <SegmentsLinkedCampaigns
              segmentedData={segmentedContentsData}
              relatedData={relatedCampaignsData}
              isViewScreen={isViewScreen}
              editSectionHandler={() => handleBasicEditSection()}
              campaignSegments={campaignStateData.segments}
            />
          )}
        </>
      )}

      {/* Activity Setup Section */}
      {campaignStateData?.activities && (
        <SummaryActivitySetup
          data={campaignStateData}
          isViewScreen={isViewScreen}
          editSectionHandler={handleActivityEditSection}
        />
      )}

      {campaignStateData?.outcomes?.length > 0 && (
        <CampaignOutcomes
          campaignStateData={campaignStateData}
          handleOutcomesEditSection={handleOutcomesEditSection}
          isViewScreen={isViewScreen}
        />
      )}

      {campaignStateData?.campaignRules?.length > 0 && (
        <CampaignRulesSummary
          campaignRules={campaignStateData?.campaignRules}
          isViewScreen={isViewScreen}
          handleOutcomesEditSection={handleOutcomesEditSection}
        />
      )}
    </>
  );
}
